import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '../components/ui/navigation-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import {
  Sun, Moon, LogOut, Settings, Calendar, Briefcase, LayoutDashboard, Bell, ChevronDown,
  Mail, HelpCircle, Users, FileText, Ticket, Menu, TrendingUp, Target, Eye, MessageSquare,
  CheckCircle2, AlertCircle, BarChart3, GraduationCap, MapPin, BookOpen, Sparkles, Tag,
  Megaphone, Plus, Compass, Utensils, Building, ClipboardList, ShoppingBag, HeartHandshake,
  MonitorPlay, Handshake, Code, Wine, Ghost, Gift, PartyPopper, Coffee, Mic2, Store, Wallet,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NOTIFICATION_ICONS = {
  proposal_viewed: <Eye className="h-4 w-4 text-blue-400" />,
  cta_click: <CheckCircle2 className="h-4 w-4 text-emerald-400" />,
  quote_accepted: <CheckCircle2 className="h-4 w-4 text-green-500" />,
  quote_declined: <AlertCircle className="h-4 w-4 text-red-400" />,
  message: <MessageSquare className="h-4 w-4 text-indigo-400" />,
};

// ---------------------------------------------------------------------------
// Information architecture (from /app/design_guidelines.json)
// ---------------------------------------------------------------------------
const DISCOVER_BROWSE = [
  { label: 'Discover Home', href: '/discover', icon: Sparkles, description: 'City magazine — guides & what\'s on' },
  { label: 'Find Events', href: '/events/denver', icon: Calendar, description: 'Browse upcoming events in your city' },
  { label: 'Experiences', href: '/ex/denver', icon: Compass, description: 'Tours, tastings, and unique activities' },
  { label: 'Reservations', href: '/res/denver', icon: Utensils, description: 'Book a table or spot' },
  { label: 'Venues', href: '/venues', icon: MapPin, description: 'Explore spaces and locations' },
];
const DISCOVER_CURATED = [
  { label: 'City Guides', href: '/denver/guides', icon: BookOpen },
  { label: 'Deals', href: '/deals', icon: Tag },
];
const DISCOVER_VIBES = [
  { label: 'Brunch', href: '/brunch', icon: Coffee },
  { label: 'Comedy', href: '/comedy', icon: Mic2 },
  { label: 'Wine', href: '/wine', icon: Wine },
  { label: 'Halloween', href: '/halloween', icon: Ghost },
  { label: 'Holiday', href: '/holiday', icon: Gift },
  { label: 'NYE', href: '/nye', icon: PartyPopper },
];
const BUSINESS_HOST = [
  { label: 'Sell Tickets', href: '/sell-tickets', icon: Ticket, description: 'Create and manage event ticketing' },
  { label: 'Venue Onboarding', href: '/venue-onboarding', icon: Building, description: 'List your venue for bookings' },
];
const BUSINESS_SERVICES = [
  { label: 'Managed Services', href: '/managed', icon: Handshake, description: 'We run your profile, events & reservations' },
  { label: 'Plan an Event', href: '/plan-event', icon: ClipboardList, description: 'Hire vendors & pros for your event' },
  { label: 'Hire Us', href: '/hire-us', icon: Briefcase, description: 'Full-service event production' },
  { label: 'Staffing', href: '/staffing', icon: Users, description: 'Event staff on demand' },
];
const BUSINESS_ECOSYSTEM = [
  { label: 'Marketplace', href: '/marketplace', icon: ShoppingBag, description: 'Supplier products & rentals' },
  { label: 'Sponsors', href: '/sponsors', icon: HeartHandshake, description: 'Put your brand at live events' },
];
const RESOURCES_LINKS = [
  { label: 'Eventism University', href: '/university', icon: GraduationCap },
  { label: 'Training', href: '/training', icon: MonitorPlay },
  { label: 'Blog', href: '/blog', icon: FileText },
  { label: 'Partners', href: '/partners', icon: Handshake },
  { label: 'Developers / API', href: '/dashboard/api-keys', icon: Code },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

function MegaLink({ item }) {
  const Icon = item.icon;
  return (
    <NavigationMenuLink asChild>
      <Link
        to={item.href}
        data-testid={`nav-link-${slug(item.label)}`}
        className="group flex items-start gap-3 rounded-lg p-2.5 hover:bg-accent/50 transition-colors"
      >
        {Icon && <Icon className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground group-hover:text-fuchsia-500 group-hover:translate-x-1 transition-all" />}
        <div className="min-w-0">
          <div className="text-sm font-medium leading-tight">{item.label}</div>
          {item.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{item.description}</p>}
        </div>
      </Link>
    </NavigationMenuLink>
  );
}

function MenuSectionTitle({ children }) {
  return <div className="px-2.5 pb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{children}</div>;
}

export default function Navbar() {
  const { user, logout, isAuthenticated, token } = useAuth();
  const { theme, mode, cycleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifCount, setNotifCount] = useState(0);

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated || !token) return;
    try {
      const [systemRes, centerRes, countRes] = await Promise.all([
        axios.get(`${API}/notifications`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { notifications: [] } })),
        axios.get(`${API}/notifications/center`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/notifications/count`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { count: 0 } })),
      ]);
      const toStr = (v) => (v == null ? '' : (typeof v === 'string' ? v : (typeof v === 'object' ? JSON.stringify(v) : String(v))));
      const systemNotifs = (systemRes.data?.notifications || []).map(n => ({
        id: n.notification_id, type: n.type, title: toStr(n.title), message: toStr(n.message),
        timestamp: n.created_at, link: typeof n.link === 'string' ? n.link : null, read: n.read,
      }));
      const centerNotifs = (Array.isArray(centerRes.data) ? centerRes.data : []).map(n => ({
        ...n, title: toStr(n.title), message: toStr(n.message),
        link: typeof n.link === 'string' ? n.link : null,
      }));
      const allNotifs = [...centerNotifs, ...systemNotifs]
        .sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''))
        .slice(0, 30);
      setNotifications(allNotifs);
      setNotifCount(countRes.data?.count || 0);
    } catch (e) { /* silent */ }
  }, [isAuthenticated, token]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  const handleNavigate = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const getDashboardLink = () => {
    if (!user) return '/dashboard';
    switch (user.role) {
      case 'admin': return '/admin';
      case 'vendor': return '/vendor';
      case 'b2b': return '/b2b';
      case 'sales_agent': return '/sales';
      case 'venue_owner': return '/venue-dashboard';
      case 'organization': return '/organization/dashboard';
      default: return '/dashboard';
    }
  };

  const dashboardLabel = user?.role === 'organization' ? 'Event Manager' : 'Dashboard';
  const isLandingPage = location.pathname === '/';
  const canSeeMarketplace = user?.role === 'admin' || user?.role === 'b2b' || user?.role === 'vendor';
  const isDark = theme === 'dark';
  const logoFilter = isDark ? 'brightness(0) invert(1)' : 'brightness(0)';
  const isOrg = user?.role === 'organization';

  const ctaHref = isOrg ? '/admin/events?new=1' : '/sell-tickets';
  const ctaLabel = isOrg ? 'Create Event' : 'Sell Tickets';
  const ctaTestId = isOrg ? 'nav-create-event' : 'nav-sell-tickets';

  return (
    <nav className={`global-navbar fixed top-0 left-0 right-0 z-50 ${isLandingPage
      ? (isDark ? 'bg-card/95 backdrop-blur-xl' : 'bg-white/95 backdrop-blur-xl border-b border-slate-200')
      : (isDark ? 'bg-background/95 backdrop-blur-xl border-b border-border' : 'bg-white/95 backdrop-blur-xl border-b border-slate-200')
    }`}>
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <div className="flex items-center gap-4 lg:gap-6">
            <Link to="/" className="flex items-center shrink-0" data-testid="navbar-logo">
              <img src="/eventism-logo.png" alt="Eventism" className="h-5 sm:h-6 md:h-7" style={{ filter: logoFilter }} />
            </Link>

            {/* Desktop Mega Menu */}
            <div className="hidden md:block">
              <NavigationMenu>
                <NavigationMenuList>
                  {/* Discover */}
                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="h-9 px-3 text-sm font-medium bg-transparent" data-testid="nav-discover-trigger">
                      Discover
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-6 p-6 w-[560px] lg:w-[680px]" data-testid="nav-discover-menu">
                        <div>
                          <MenuSectionTitle>Browse</MenuSectionTitle>
                          <div className="flex flex-col gap-0.5">
                            {DISCOVER_BROWSE.map((item) => <MegaLink key={item.label} item={item} />)}
                          </div>
                        </div>
                        <div className="flex flex-col gap-5">
                          <div>
                            <MenuSectionTitle>Curated</MenuSectionTitle>
                            <div className="flex flex-col gap-0.5">
                              {DISCOVER_CURATED.map((item) => <MegaLink key={item.label} item={item} />)}
                            </div>
                          </div>
                          <div>
                            <MenuSectionTitle>Seasons &amp; Vibes</MenuSectionTitle>
                            <div className="flex flex-wrap gap-1.5 px-2.5">
                              {DISCOVER_VIBES.map((item) => (
                                <NavigationMenuLink asChild key={item.label}>
                                  <Link
                                    to={item.href}
                                    data-testid={`nav-vibe-${slug(item.label)}`}
                                    className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium hover:bg-accent/50 hover:border-fuchsia-500/40 transition-colors"
                                  >
                                    <item.icon className="h-3 w-3 text-muted-foreground" />
                                    {item.label}
                                  </Link>
                                </NavigationMenuLink>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  {/* For Business */}
                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="h-9 px-3 text-sm font-medium bg-transparent" data-testid="nav-business-trigger">
                      For Business
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="grid grid-cols-2 gap-6 p-6 w-[540px]" data-testid="nav-business-menu">
                        <div className="flex flex-col gap-5">
                          <div>
                            <MenuSectionTitle>Host &amp; Sell</MenuSectionTitle>
                            <div className="flex flex-col gap-0.5">
                              {BUSINESS_HOST.map((item) => <MegaLink key={item.label} item={item} />)}
                            </div>
                          </div>
                          <div>
                            <MenuSectionTitle>Ecosystem</MenuSectionTitle>
                            <div className="flex flex-col gap-0.5">
                              {BUSINESS_ECOSYSTEM.map((item) => <MegaLink key={item.label} item={item} />)}
                            </div>
                          </div>
                        </div>
                        <div>
                          <MenuSectionTitle>Services</MenuSectionTitle>
                          <div className="flex flex-col gap-0.5">
                            {BUSINESS_SERVICES.map((item) => <MegaLink key={item.label} item={item} />)}
                          </div>
                        </div>
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  {/* Resources */}
                  <NavigationMenuItem>
                    <NavigationMenuTrigger className="h-9 px-3 text-sm font-medium bg-transparent" data-testid="nav-resources-trigger">
                      Resources
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <div className="flex flex-col gap-0.5 p-3 w-[280px]" data-testid="nav-resources-menu">
                        <MenuSectionTitle>Learn &amp; Connect</MenuSectionTitle>
                        {RESOURCES_LINKS.map((item) => <MegaLink key={item.label} item={item} />)}
                      </div>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>
            </div>
          </div>

          {/* Desktop right-side actions */}
          <div className="hidden md:flex items-center gap-1">
            {/* Supplier Hub — standalone role-gated link */}
            {canSeeMarketplace && (
              <Link to="/marketplace">
                <Button variant="ghost" size="sm" className="text-sm" data-testid="nav-supplier-hub">
                  <Store className="mr-1.5 h-4 w-4" />
                  Supplier Hub
                </Button>
              </Link>
            )}

            {/* Notifications Bell */}
            {isAuthenticated && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-full relative h-8 w-8"
                    data-testid="notifications-bell"
                    onClick={fetchNotifications}
                  >
                    <Bell className="h-4 w-4" />
                    {notifCount > 0 && (
                      <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-[9px] font-bold text-foreground flex items-center justify-center">
                        {notifCount > 9 ? '9+' : notifCount}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
                  <div className="px-3 py-2 flex items-center justify-between border-b">
                    <span className="font-semibold text-sm">Notifications</span>
                    <Badge variant="outline" className="text-xs">{notifCount} new</Badge>
                  </div>
                  {notifications.length === 0 ? (
                    <div className="px-3 py-8 text-center text-muted-foreground text-sm">
                      No notifications yet
                    </div>
                  ) : (
                    notifications.slice(0, 15).map((notification, index) => (
                      <DropdownMenuItem
                        key={notification.id || index}
                        className="px-3 py-3 cursor-pointer"
                        onClick={() => notification.link && navigate(notification.link)}
                      >
                        <div className="flex gap-3 w-full">
                          <div className="flex-shrink-0 mt-0.5">
                            {NOTIFICATION_ICONS[notification.type] || <Bell className="h-4 w-4 text-muted-foreground" />}
                          </div>
                          <div className="flex flex-col gap-1 min-w-0">
                            <span className="font-medium text-sm">{notification.title}</span>
                            <span className="text-xs text-muted-foreground line-clamp-2">{notification.message}</span>
                            {notification.timestamp && (
                              <span className="text-[10px] text-muted-foreground">
                                {new Date(notification.timestamp).toLocaleString()}
                              </span>
                            )}
                          </div>
                        </div>
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={cycleTheme}
              className="rounded-full h-8 w-8 relative"
              data-testid="theme-toggle"
              title={`Theme: ${mode === 'auto' ? 'Auto (Denver)' : mode}`}
            >
              {mode === 'auto' ? (
                <span className="relative">
                  {theme === 'dark' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                  <span className="absolute -bottom-1 -right-1.5 text-[8px] font-bold bg-indigo-500 text-white rounded px-0.5 leading-tight">A</span>
                </span>
              ) : theme === 'dark' ? (
                <Moon className="h-4 w-4" />
              ) : (
                <Sun className="h-4 w-4" />
              )}
            </Button>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center space-x-2 rounded-full h-8 px-2" data-testid="user-menu-trigger">
                    {user?.picture ? (
                      <img src={user.picture} alt={user.name} className="w-6 h-6 rounded-full" />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-indigo-500 flex items-center justify-center text-foreground text-xs font-medium">
                        {user?.name?.[0]?.toUpperCase() || 'U'}
                      </div>
                    )}
                    <span className="hidden lg:block text-xs font-medium max-w-[80px] truncate">{user?.name}</span>
                    <ChevronDown className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 max-h-[80vh] overflow-y-auto">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                    <p className="text-xs text-indigo-500 capitalize mt-1">{user?.role}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(getDashboardLink())} data-testid="menu-dashboard">
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    {dashboardLabel}
                  </DropdownMenuItem>
                  {isOrg && (
                    <DropdownMenuItem onClick={() => navigate('/admin/events')} data-testid="menu-my-events">
                      <Calendar className="mr-2 h-4 w-4" />
                      My Events
                    </DropdownMenuItem>
                  )}
                  {user?.role === 'admin' && (
                    <DropdownMenuItem onClick={() => navigate('/admin/platform-financials')} data-testid="menu-platform-financials">
                      <Wallet className="mr-2 h-4 w-4" />
                      Platform Financials
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate('/my-events')} data-testid="menu-my-bookings">
                    <Calendar className="mr-2 h-4 w-4" />
                    My Bookings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/my-tickets')} data-testid="menu-my-tickets">
                    <Ticket className="mr-2 h-4 w-4" />
                    My Tickets
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/money')} data-testid="menu-my-wallet">
                    <Wallet className="mr-2 h-4 w-4" />
                    My Wallet
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/my-invoices')} data-testid="menu-my-invoices">
                    <FileText className="mr-2 h-4 w-4" />
                    My Invoices
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/my/memberships')} data-testid="menu-my-memberships">
                    <Sparkles className="mr-2 h-4 w-4" />
                    My Memberships
                  </DropdownMenuItem>
                  {user?.street_team_enabled && (
                    <DropdownMenuItem onClick={() => navigate('/dashboard/street-team')} data-testid="menu-street-team">
                      <Megaphone className="mr-2 h-4 w-4" />
                      Street Team
                    </DropdownMenuItem>
                  )}
                  {user?.role === 'admin' && (
                    <DropdownMenuItem onClick={() => navigate('/admin/events')} data-testid="menu-admin-events">
                      <Ticket className="mr-2 h-4 w-4" />
                      Events Manager
                    </DropdownMenuItem>
                  )}
                  {user?.role === 'admin' && (
                    <DropdownMenuItem onClick={() => navigate('/admin/sms-inbox')} data-testid="menu-sms-inbox">
                      <MessageSquare className="mr-2 h-4 w-4" />
                      SMS Inbox
                    </DropdownMenuItem>
                  )}
                  {(user?.role === 'admin' || user?.role === 'staff') && (
                    <DropdownMenuItem onClick={() => navigate('/admin/workspaces')} data-testid="menu-workspaces">
                      <Briefcase className="mr-2 h-4 w-4" />
                      Event Workspaces
                    </DropdownMenuItem>
                  )}
                  {user?.role === 'admin' && (
                    <DropdownMenuItem onClick={() => navigate('/admin/analytics')} data-testid="menu-analytics">
                      <TrendingUp className="mr-2 h-4 w-4" />
                      Analytics
                    </DropdownMenuItem>
                  )}
                  {user?.role === 'admin' && (
                    <DropdownMenuItem onClick={() => navigate('/admin/pipeline')} data-testid="menu-pipeline">
                      <Target className="mr-2 h-4 w-4" />
                      Sales Pipeline
                    </DropdownMenuItem>
                  )}
                  {user?.role === 'admin' && (
                    <DropdownMenuItem onClick={() => navigate('/admin/blog')} data-testid="menu-blog-manager">
                      <FileText className="mr-2 h-4 w-4" />
                      Blog Manager
                    </DropdownMenuItem>
                  )}
                  {user?.role === 'admin' && (
                    <DropdownMenuItem onClick={() => navigate('/admin/guides')} data-testid="menu-editorial-guides">
                      <BookOpen className="mr-2 h-4 w-4" />
                      Editorial Guides
                    </DropdownMenuItem>
                  )}
                  {user?.role === 'admin' && (
                    <DropdownMenuItem onClick={() => navigate('/admin/supplier-performance')} data-testid="menu-supplier-performance">
                      <BarChart3 className="mr-2 h-4 w-4" />
                      Supplier Performance
                    </DropdownMenuItem>
                  )}
                  {user?.role === 'admin' && (
                    <DropdownMenuItem onClick={() => navigate('/admin/sales-agents')} data-testid="menu-sales-agents">
                      <Users className="mr-2 h-4 w-4" />
                      Sales Team
                    </DropdownMenuItem>
                  )}
                  {user?.role === 'admin' && (
                    <DropdownMenuItem onClick={() => navigate('/admin/ai-agents')} data-testid="menu-ai-agents">
                      <Briefcase className="mr-2 h-4 w-4" />
                      AI Agents
                    </DropdownMenuItem>
                  )}
                  {user?.role === 'admin' && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/admin/promo-codes')} data-testid="menu-promo-codes">
                        <Tag className="mr-2 h-4 w-4" />
                        Promo Codes
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/ad-creator')} data-testid="menu-ad-creator">
                        <Megaphone className="mr-2 h-4 w-4" />
                        Ad Creator
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/site-directory')} data-testid="menu-site-directory">
                        <MapPin className="mr-2 h-4 w-4" />
                        Site Directory
                      </DropdownMenuItem>
                    </>
                  )}
                  {user?.role === 'vendor' && (
                    <DropdownMenuItem onClick={() => navigate('/vendor/profile')} data-testid="menu-vendor-profile">
                      <Briefcase className="mr-2 h-4 w-4" />
                      Vendor Profile
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate(user?.role === 'sales_agent' ? '/sales/settings' : '/settings')} data-testid="menu-settings">
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/university')} data-testid="menu-university">
                    <GraduationCap className="mr-2 h-4 w-4" />
                    Eventism University
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-500" data-testid="menu-logout">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => navigate('/login')} data-testid="nav-login" className="text-sm">
                Sign In
              </Button>
            )}

            {/* Primary CTA */}
            <Link to={ctaHref} className="ml-1">
              <Button
                size="sm"
                className="rounded-full px-4 text-xs sm:text-sm text-white bg-gradient-to-r from-fuchsia-500 via-indigo-500 to-amber-500 hover:opacity-90 shadow-[0_0_16px_-4px_rgba(217,70,239,0.5)] transition-opacity"
                data-testid={ctaTestId}
              >
                {isOrg && <Plus className="mr-1 h-3.5 w-3.5" />}
                {ctaLabel}
              </Button>
            </Link>
          </div>

          {/* Mobile */}
          <div className="flex md:hidden items-center space-x-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={cycleTheme}
              className="rounded-full h-8 w-8 relative"
              data-testid="mobile-theme-toggle"
            >
              {mode === 'auto' ? (
                <span className="relative">
                  {theme === 'dark' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                  <span className="absolute -bottom-1 -right-1.5 text-[8px] font-bold bg-indigo-500 text-white rounded px-0.5 leading-tight">A</span>
                </span>
              ) : theme === 'dark' ? (
                <Moon className="h-4 w-4" />
              ) : (
                <Sun className="h-4 w-4" />
              )}
            </Button>

            {isAuthenticated && (
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full relative h-8 w-8"
                data-testid="mobile-notifications-bell"
                onClick={() => { fetchNotifications(); handleNavigate('/notifications'); }}
              >
                <Bell className="h-4 w-4" />
                {notifCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-[9px] font-bold text-foreground flex items-center justify-center">
                    {notifCount > 9 ? '9+' : notifCount}
                  </span>
                )}
              </Button>
            )}

            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="mobile-menu-toggle">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] sm:w-[350px] flex flex-col p-0">
                <SheetHeader className="px-4 pt-4">
                  <SheetTitle className="text-left">
                    <img src="/eventism-logo.png" alt="Eventism" className="h-5 sm:h-6" style={{ filter: logoFilter }} />
                  </SheetTitle>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto px-4 pb-4">
                  {/* User Info */}
                  {isAuthenticated && user && (
                    <div className="px-2 py-3 mt-4 bg-muted/50 rounded-lg" data-testid="mobile-user-card">
                      <div className="flex items-center space-x-3">
                        {user.picture ? (
                          <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-indigo-500 flex items-center justify-center text-foreground font-medium">
                            {user.name?.[0]?.toUpperCase() || 'U'}
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-sm">{user.name}</p>
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                          <p className="text-xs text-indigo-500 capitalize">{user.role}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Grouped IA */}
                  <Accordion type="multiple" defaultValue={['discover']} className="mt-3">
                    <AccordionItem value="discover">
                      <AccordionTrigger className="text-sm font-semibold py-3" data-testid="mobile-nav-discover">Discover</AccordionTrigger>
                      <AccordionContent>
                        <div className="flex flex-col">
                          {[...DISCOVER_BROWSE, ...DISCOVER_CURATED].map((item) => (
                            <Button key={item.label} variant="ghost" size="sm" className="justify-start h-9" onClick={() => handleNavigate(item.href)} data-testid={`mobile-link-${slug(item.label)}`}>
                              <item.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                              {item.label}
                            </Button>
                          ))}
                          <div className="flex flex-wrap gap-1.5 px-3 pt-2 pb-1">
                            {DISCOVER_VIBES.map((item) => (
                              <button
                                key={item.label}
                                onClick={() => handleNavigate(item.href)}
                                data-testid={`mobile-vibe-${slug(item.label)}`}
                                className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-xs font-medium hover:bg-accent/50"
                              >
                                <item.icon className="h-3 w-3 text-muted-foreground" />
                                {item.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="business">
                      <AccordionTrigger className="text-sm font-semibold py-3" data-testid="mobile-nav-business">For Business</AccordionTrigger>
                      <AccordionContent>
                        <div className="flex flex-col">
                          {[...BUSINESS_HOST, ...BUSINESS_SERVICES, ...BUSINESS_ECOSYSTEM].map((item) => (
                            <Button key={item.label} variant="ghost" size="sm" className="justify-start h-9" onClick={() => handleNavigate(item.href)} data-testid={`mobile-link-${slug(item.label)}`}>
                              <item.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                              {item.label}
                            </Button>
                          ))}
                          {canSeeMarketplace && (
                            <Button variant="ghost" size="sm" className="justify-start h-9" onClick={() => handleNavigate('/marketplace')} data-testid="mobile-nav-supplier-hub">
                              <Store className="mr-2 h-4 w-4 text-muted-foreground" />
                              Supplier Hub
                            </Button>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="resources">
                      <AccordionTrigger className="text-sm font-semibold py-3" data-testid="mobile-nav-resources">Resources</AccordionTrigger>
                      <AccordionContent>
                        <div className="flex flex-col">
                          {RESOURCES_LINKS.map((item) => (
                            <Button key={item.label} variant="ghost" size="sm" className="justify-start h-9" onClick={() => handleNavigate(item.href)} data-testid={`mobile-link-${slug(item.label)}`}>
                              <item.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                              {item.label}
                            </Button>
                          ))}
                          <Button variant="ghost" size="sm" className="justify-start h-9" onClick={() => handleNavigate('/how-it-works')}>
                            <HelpCircle className="mr-2 h-4 w-4 text-muted-foreground" />
                            How It Works
                          </Button>
                          <Button variant="ghost" size="sm" className="justify-start h-9" onClick={() => handleNavigate('/about')}>
                            <Users className="mr-2 h-4 w-4 text-muted-foreground" />
                            About Us
                          </Button>
                          <Button variant="ghost" size="sm" className="justify-start h-9" onClick={() => handleNavigate('/contact')}>
                            <Mail className="mr-2 h-4 w-4 text-muted-foreground" />
                            Contact Us
                          </Button>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    {isAuthenticated && (
                      <AccordionItem value="account">
                        <AccordionTrigger className="text-sm font-semibold py-3" data-testid="mobile-nav-account">My Account</AccordionTrigger>
                        <AccordionContent>
                          <div className="flex flex-col">
                            <Button variant="ghost" size="sm" className="justify-start h-9" onClick={() => handleNavigate(getDashboardLink())} data-testid="mobile-menu-dashboard">
                              <LayoutDashboard className="mr-2 h-4 w-4 text-muted-foreground" />
                              {dashboardLabel}
                            </Button>
                            {isOrg && (
                              <Button variant="ghost" size="sm" className="justify-start h-9" onClick={() => handleNavigate('/admin/events')} data-testid="mobile-nav-my-events">
                                <Calendar className="mr-2 h-4 w-4 text-muted-foreground" />
                                My Events
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" className="justify-start h-9" onClick={() => handleNavigate('/my-events')} data-testid="mobile-menu-my-bookings">
                              <Calendar className="mr-2 h-4 w-4 text-muted-foreground" />
                              My Bookings
                            </Button>
                            <Button variant="ghost" size="sm" className="justify-start h-9" onClick={() => handleNavigate('/my-tickets')} data-testid="mobile-menu-my-tickets">
                              <Ticket className="mr-2 h-4 w-4 text-muted-foreground" />
                              My Tickets
                            </Button>
                            <Button variant="ghost" size="sm" className="justify-start h-9" onClick={() => handleNavigate('/my/memberships')} data-testid="mobile-menu-my-memberships">
                              <Sparkles className="mr-2 h-4 w-4 text-muted-foreground" />
                              My Memberships
                            </Button>
                            {user?.street_team_enabled && (
                              <Button variant="ghost" size="sm" className="justify-start h-9" onClick={() => handleNavigate('/dashboard/street-team')} data-testid="mobile-menu-street-team">
                                <Megaphone className="mr-2 h-4 w-4 text-muted-foreground" />
                                Street Team
                              </Button>
                            )}
                            {user?.role === 'vendor' && (
                              <Button variant="ghost" size="sm" className="justify-start h-9" onClick={() => handleNavigate('/vendor/profile')} data-testid="mobile-menu-vendor-profile">
                                <Briefcase className="mr-2 h-4 w-4 text-muted-foreground" />
                                Vendor Profile
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" className="justify-start h-9" onClick={() => handleNavigate(user?.role === 'sales_agent' ? '/sales/settings' : '/settings')} data-testid="mobile-menu-settings">
                              <Settings className="mr-2 h-4 w-4 text-muted-foreground" />
                              Settings
                            </Button>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    )}

                    {user?.role === 'admin' && (
                      <AccordionItem value="admin">
                        <AccordionTrigger className="text-sm font-semibold py-3" data-testid="mobile-nav-admin">Admin</AccordionTrigger>
                        <AccordionContent>
                          <div className="flex flex-col">
                            <Button variant="ghost" size="sm" className="justify-start h-9" onClick={() => handleNavigate('/admin/events')} data-testid="mobile-menu-admin-events">
                              <Ticket className="mr-2 h-4 w-4 text-muted-foreground" /> Events Manager
                            </Button>
                            <Button variant="ghost" size="sm" className="justify-start h-9" onClick={() => handleNavigate('/admin/sms-inbox')} data-testid="mobile-menu-sms-inbox">
                              <MessageSquare className="mr-2 h-4 w-4 text-muted-foreground" /> SMS Inbox
                            </Button>
                            <Button variant="ghost" size="sm" className="justify-start h-9" onClick={() => handleNavigate('/admin/workspaces')} data-testid="mobile-menu-workspaces">
                              <Briefcase className="mr-2 h-4 w-4 text-muted-foreground" /> Event Workspaces
                            </Button>
                            <Button variant="ghost" size="sm" className="justify-start h-9" onClick={() => handleNavigate('/admin/analytics')} data-testid="mobile-menu-analytics">
                              <TrendingUp className="mr-2 h-4 w-4 text-muted-foreground" /> Analytics
                            </Button>
                            <Button variant="ghost" size="sm" className="justify-start h-9" onClick={() => handleNavigate('/admin/pipeline')} data-testid="mobile-menu-pipeline">
                              <Target className="mr-2 h-4 w-4 text-muted-foreground" /> Sales Pipeline
                            </Button>
                            <Button variant="ghost" size="sm" className="justify-start h-9" onClick={() => handleNavigate('/admin/blog')} data-testid="mobile-menu-blog-manager">
                              <FileText className="mr-2 h-4 w-4 text-muted-foreground" /> Blog Manager
                            </Button>
                            <Button variant="ghost" size="sm" className="justify-start h-9" onClick={() => handleNavigate('/admin/guides')} data-testid="mobile-menu-editorial-guides">
                              <BookOpen className="mr-2 h-4 w-4 text-muted-foreground" /> Editorial Guides
                            </Button>
                            <Button variant="ghost" size="sm" className="justify-start h-9" onClick={() => handleNavigate('/admin/supplier-performance')} data-testid="mobile-menu-supplier-performance">
                              <BarChart3 className="mr-2 h-4 w-4 text-muted-foreground" /> Supplier Performance
                            </Button>
                            <Button variant="ghost" size="sm" className="justify-start h-9" onClick={() => handleNavigate('/admin/sales-agents')} data-testid="mobile-menu-sales-agents">
                              <Users className="mr-2 h-4 w-4 text-muted-foreground" /> Sales Team
                            </Button>
                            <Button variant="ghost" size="sm" className="justify-start h-9" onClick={() => handleNavigate('/admin/ai-agents')} data-testid="mobile-menu-ai-agents">
                              <Briefcase className="mr-2 h-4 w-4 text-muted-foreground" /> AI Agents
                            </Button>
                            <Button variant="ghost" size="sm" className="justify-start h-9" onClick={() => handleNavigate('/admin/promo-codes')} data-testid="mobile-menu-promo-codes">
                              <Tag className="mr-2 h-4 w-4 text-muted-foreground" /> Promo Codes
                            </Button>
                            <Button variant="ghost" size="sm" className="justify-start h-9" onClick={() => handleNavigate('/admin/ad-creator')} data-testid="mobile-menu-ad-creator">
                              <Megaphone className="mr-2 h-4 w-4 text-muted-foreground" /> Ad Creator
                            </Button>
                            <Button variant="ghost" size="sm" className="justify-start h-9" onClick={() => handleNavigate('/admin/site-directory')} data-testid="mobile-menu-site-directory">
                              <MapPin className="mr-2 h-4 w-4 text-muted-foreground" /> Site Directory
                            </Button>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    )}
                  </Accordion>

                  {(user?.role === 'admin' || user?.role === 'staff') && user?.role !== 'admin' && (
                    <Button variant="ghost" size="sm" className="justify-start h-9 w-full mt-1" onClick={() => handleNavigate('/admin/workspaces')} data-testid="mobile-menu-workspaces-staff">
                      <Briefcase className="mr-2 h-4 w-4 text-muted-foreground" /> Event Workspaces
                    </Button>
                  )}

                  {!isAuthenticated && (
                    <Button variant="ghost" className="justify-start w-full mt-3" onClick={() => handleNavigate('/login')} data-testid="mobile-nav-login">
                      Sign In
                    </Button>
                  )}

                  {isAuthenticated && (
                    <Button variant="ghost" className="justify-start w-full mt-3 text-red-500" onClick={handleLogout} data-testid="mobile-menu-logout">
                      <LogOut className="mr-2 h-4 w-4" />
                      Logout
                    </Button>
                  )}
                </div>

                {/* Pinned CTA */}
                <div className="border-t border-border p-4">
                  <Button
                    className="w-full rounded-full text-white bg-gradient-to-r from-fuchsia-500 via-indigo-500 to-amber-500 hover:opacity-90"
                    onClick={() => handleNavigate(ctaHref)}
                    data-testid={isOrg ? 'mobile-nav-create-event' : 'mobile-nav-sell-tickets'}
                  >
                    {isOrg && <Plus className="mr-1.5 h-4 w-4" />}
                    {ctaLabel}
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
